import React, { useRef, useState, KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email)
}

interface CcEmailInputProps {
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  isReadonly?: boolean
  placeholder?: string
  className?: string
}

export function CcEmailInput({
  value,
  onChange,
  disabled = false,
  isReadonly = false,
  placeholder = 'Enter email address',
  className,
}: CcEmailInputProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const addEmail = (raw: string) => {
    const email = raw.trim()
    if (!email) return
    if (!value.includes(email)) {
      onChange([...value, email])
    }
    setInputValue('')
  }

  const removeEmail = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault()
      addEmail(inputValue)
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      removeEmail(value.length - 1)
    }
  }

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    const emails = pasted.split(/[\s,;]+/).filter(Boolean)
    const unique = emails.filter(email => !value.includes(email))
    if (unique.length > 0) {
      onChange([...value, ...unique])
    }
    setInputValue('')
  }

  if (isReadonly) {
    return (
      <p className={cn('text-sm text-foreground', className)}>
        {value.length > 0 ? value.join(', ') : <span className="text-muted-foreground">—</span>}
      </p>
    )
  }

  return (
    <div
      className={cn(
        'flex min-h-10 w-full flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
        'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        (disabled) && 'cursor-not-allowed opacity-50',
        className
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((email, index) => {
        const valid = isValidEmail(email)
        return (
          <span
            key={index}
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
              valid
                ? 'bg-secondary text-secondary-foreground'
                : 'border border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
            )}
          >
            {email}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeEmail(index) }}
                className="ml-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/20"
                aria-label={`Remove ${email}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        )
      })}
      {!disabled && (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          className="min-w-32 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
      )}
    </div>
  )
}
